/* eslint-disable @typescript-eslint/no-var-requires */
import spotlightImageSrc from '../images/spotlight.svg';
import { renderNoMessages } from './messages/no-messages';
import { renderSpotlightMessage } from './messages/spotlight-message';
import { translate } from '../i18n';
import { metricsPublisher } from '../util/metrics';
import KatalMetricsPublisher from '@amzn/katal-metrics/lib/KatalMetricsPublisher';
import '../styles/spotlight.scss';
import { renderError } from 'src/components/error';
import {
  WebsiteCommunication,
} from '@amzn/brand-registry-communication-service-type-script-client';
import { renderSpinner } from 'src/components/spinner';
import { recordEvents } from '../util/record-communication-event';
import { getCommunicationStatus, setLocalStorageStatus } from 'src/util/local-storage';

let spotlightPublisher: KatalMetricsPublisher;

function hasSpotlightMessages(communications: WebsiteCommunication[]): boolean {
  return communications[0]?.communicationId !== undefined;
}

const createSpotlightButton = (): HTMLButtonElement => {
  const spotlightButton = document.createElement('button');
  spotlightButton.id = 'br-spotlight-button';
  spotlightButton.setAttribute('aria-label', translate('br-trim-see-spotlight-button'));
  spotlightButton.classList.add('hover-brightness');

  const spotlightIcon = document.createElement('div');
  spotlightIcon.classList.add(
    'tw-br-trim-bg-transparent',
    'tw-br-trim-self-center',
    'tw-br-trim-flex',
    'tw-br-trim-h-[20px]',
    'tw-br-trim-w-[20px]'
  );

  const iconImage = document.createElement('img');
  iconImage.setAttribute('src', spotlightImageSrc);
  spotlightIcon.append(iconImage);
  spotlightButton.append(spotlightIcon);

  return spotlightButton;
};

export function renderSpotlight (
  allowAutoOpen: boolean,
  isLoading: boolean,
  hasError: boolean,
  communications: WebsiteCommunication[] = []
): HTMLDivElement {

  let hasRecordedAllDisplayedEvents = false;

  spotlightPublisher = metricsPublisher.newChildActionPublisherForMethod('Spotlight');

  const spotlight = document.createElement('div');
  spotlight.id = 'brand-registry-spotlight-container';
  spotlight.classList.add('tw-br-trim-flex', 'tw-br-trim-items-center');

  const spotlightButton = createSpotlightButton();

  spotlight.append(spotlightButton);

  const popoverContainer = document.createElement('div');
  popoverContainer.id = 'br-spotlight-popover';
  popoverContainer.classList.add(
    'tw-br-trim-divide-x-0',
    'tw-br-trim-divide-y',
    'tw-br-trim-divide-solid',
    'tw-br-trim-divide-athens'
  );

  const popoverBackground = document.createElement('div');
  popoverBackground.id = 'br-spotlight-popover-background';
  popoverBackground.onclick = toggleSpotlight;

  spotlight.append(popoverBackground);
  spotlight.append(popoverContainer);

  const viewAll = document.createElement('div');
  viewAll.id = 'br-spotlight-view-all';
  viewAll.classList.add('tw-br-trim-text-eastern');

  const communicationIds: string[] = communications
    .map(({ communicationId }) => communicationId);

  const unseenCommunications = communications
    .filter(communication => getCommunicationStatus(communication.communicationId, communication.status) === 'SENT');

  const firstUnseenMessage = unseenCommunications.length && renderSpotlightMessage(unseenCommunications[0]);

  function toggleSpotlight() {

    if (popoverContainer.classList.contains('visible')) {
      popoverContainer.classList.remove('visible');
      spotlightPublisher.publishCounter('SpotlightClosed', 1);
    } else {
      popoverContainer.classList.add('visible');
      spotlightPublisher.publishCounter('SpotlightOpened', 1);

      const isOnlyShowingFirstUnreadMessage = viewAll.isConnected;

      if (!hasRecordedAllDisplayedEvents && !isOnlyShowingFirstUnreadMessage) {
        recordEvents(communicationIds, 'DISPLAYED');
        unseenCommunications.forEach(({ communicationId }) => setLocalStorageStatus(communicationId, 'DISPLAYED'));
        hasRecordedAllDisplayedEvents = true;
      }
    }
    spotlightButton.classList.remove('has-unread');
  }

  const popoverHeader = document.createElement('div');
  popoverHeader.classList.add(
    'tw-br-trim-flex', 'tw-br-trim-justify-between', 'tw-br-trim-items-center',
    'tw-br-trim-px-[14px]', 'tw-br-trim-py-[8px]'
  );

  const headerText = document.createElement('div');
  headerText.id = 'br-spotlight-popover-header-text';
  headerText.classList.add(
    'tw-br-trim-text-black', 'tw-br-trim-text-[14px]', 'tw-br-trim-font-bold', 'tw-br-trim-m-0',
    'tw-br-trim-min-w-[62px]'
  );
  headerText.append(translate('br-spotlight-popover-header-text'));

  const popoverCloseButton = document.createElement('button');
  popoverCloseButton.id = 'br-spotlight-close-button';

  const closeIconContainer = document.createElement('div');
  closeIconContainer.classList.add('tw-br-trim-flex');
  const closeIcon = document.createElement('img');
  closeIcon.classList.add(
    'tw-br-trim-w-[20px]', 'tw-br-trim-h-[20px]'
  );
  closeIcon.setAttribute('aria-label', 'Close spotlight');
  closeIcon.src = require('../images/close-Icon.svg').default;

  spotlightButton.onclick = () => {
    toggleSpotlight();
  };

  popoverCloseButton.onclick = () => {
    toggleSpotlight();
    spotlightButton.classList.remove('has-unread');
  };

  popoverContainer.append(popoverHeader);

  popoverHeader.append(headerText);
  popoverHeader.append(popoverCloseButton);

  popoverCloseButton.append(closeIconContainer);
  closeIconContainer.append(closeIcon);

  // if spotlight is clicked out of, close it
  document.addEventListener('click', (e) => {
    const spotlightContainer = document.getElementById('brand-registry-spotlight-container');

    if (!spotlightContainer) return;

    if (!e.composedPath().includes(spotlightContainer) && popoverContainer.classList.contains('visible')) {
      toggleSpotlight();
      spotlightPublisher.publishCounter('SpotlightClosedClickedOutside', 1);
    }
  });

  if (isLoading) {
    const spinnerDiv = document.createElement('div');
    spinnerDiv.classList.add('tw-br-trim-pt-10');
    const spinner = renderSpinner();
    spinnerDiv.append(spinner);

    popoverContainer.append(spinnerDiv);

    return spotlight;
  }

  const messagesContainer = document.createElement('div');
  messagesContainer.id = 'br-spotlight-messages-container';

  if (hasError) {
    const errorContainer = document.createElement('div');
    errorContainer.classList.add('tw-br-trim-m-5');
    errorContainer.append(renderError());

    messagesContainer.append(errorContainer);
    popoverContainer.append(messagesContainer);

    return spotlight;
  }

  if (!hasSpotlightMessages(communications)) {
    messagesContainer.append(renderNoMessages());
    popoverContainer.append(messagesContainer);

    return spotlight;
  }

  const messages = document.createElement('div');
  messages.id = 'br-spotlight-messages';
  messages.classList.add(
    'tw-br-trim-divide-x-0',
    'tw-br-trim-divide-y',
    'tw-br-trim-divide-solid',
    'tw-br-trim-divide-athens'
  );

  messagesContainer.append(messages);
  popoverContainer.append(messagesContainer);

  const showAllMessages = (shouldRecordEvents = false) => {
    messages.replaceChildren(...communications.map(renderSpotlightMessage));

    // only want to record events when this is initiated by "View all" otherwise they will already be published
    // in toggleSpotlight
    if (shouldRecordEvents && !hasRecordedAllDisplayedEvents) {
      const communicationIdsMinusFirstUnread = communicationIds
        .filter((id) => id !== unseenCommunications[0].communicationId);
      recordEvents(communicationIdsMinusFirstUnread, 'DISPLAYED');
      unseenCommunications.forEach(({ communicationId }) => {
        if (communicationId !== unseenCommunications[0].communicationId ) {
          setLocalStorageStatus(communicationId, 'DISPLAYED');
        }
      });
      hasRecordedAllDisplayedEvents = true;
    }
  };

  const hasUnreadMessages = !!firstUnseenMessage;

  const totalMessages = communications.length;

  if (hasUnreadMessages && allowAutoOpen) {
    spotlightButton.classList.add('has-unread');
    popoverContainer.classList.add('visible');

    // add outline to first unseen message
    firstUnseenMessage.classList.add(
      'tw-br-trim-outline',
      'tw-br-trim-outline-2',
      'tw-br-trim-outline-offset-[-2px]',
      'tw-br-trim-outline-[#0e7490]'
    );

    // send a request to record event
    recordEvents([unseenCommunications[0].communicationId], 'DISPLAYED');
    setLocalStorageStatus(unseenCommunications[0].communicationId, 'DISPLAYED');

    // if there is only one message no need to show "View all"
    if (totalMessages > 1) {
      const viewAllButton = document.createElement('button');
      viewAllButton.id = 'br-spotlight-view-all-button';

      viewAllButton.onclick = () => {
        showAllMessages(true);
        viewAll.remove();
        spotlightButton.classList.remove('has-unread');
      };

      viewAllButton.append(
        translate('br-spotlight-view-all', { totalMessages })
      );
      viewAll.append(viewAllButton);

      popoverContainer.append(viewAll);
    }

    messages.append(firstUnseenMessage);
  } else {
    showAllMessages();
  }

  messagesContainer.append(messages);

  return spotlight;
}
